import { createApp } from 'vue';
import { createHead } from '@unhead/vue';
import { DefaultApolloClient } from '@vue/apollo-composable';
import FloatingVue from 'floating-vue';
import 'floating-vue/dist/style.css';

import pkg from '@freelancerepublik/version/package.json';

import clickoutDirective from '@/directives/clickout';

import { AuthPlugin } from '@/plugins/auth';
import { cloudinaryPlugin } from '@/plugins/cloudinary';
import { loggerPlugin } from '@/plugins/logger';
import { modalPlugin } from '@/plugins/modal';
import { placesPlugin } from '@/plugins/places';
import { rudderstackPlugin } from '@/plugins/rudderstack';
import { sentryPlugin } from '@/plugins/sentry';
import { gtmPlugin } from '@/ui/plugins/gtm';

import { createApolloClient } from '@/infra/apolloClient';
import { initSentry } from '@/infra/sentryClient';

import { ENV } from './env';
import './assets/styles/tailwind.css';

import { createAppRouter } from './router';
import App from './ui/App.vue';
import { createAppPinia } from './store';

const sentry = initSentry({
  project: 'ui-app',
  version: pkg.version,
  environment: ENV.CONFIG_ENV,
  dsn: ENV.SENTRY_DSN,
});

const app = createApp(App);

const head = createHead();
app.use(head);

const pinia = createAppPinia();
app.use(pinia);

// Router (installed later, after AuthPlugin, so guards can access $auth)
const router = createAppRouter({ sentry, modal: modalPlugin._instance, gtm: gtmPlugin._instance });

// Directives
app.directive('clickout', clickoutDirective);

// Floating Vue (replaces v-tooltip)
app.use(FloatingVue);

// Plugins via provide/inject
app.use(cloudinaryPlugin);
app.use(loggerPlugin);
app.use(modalPlugin);
app.use(sentryPlugin, { sentry });
app.use(rudderstackPlugin);
app.use(gtmPlugin, {
  id: ENV.GTM_ID,
  enabled: ENV.CONFIG_ENV === 'production',
  debug: false,
});
app.use(placesPlugin, { apiKey: ENV.GOOGLE_API_KEY });

(async () => {
  const apolloClient = await createApolloClient({
    apiUri: ENV.API_URL,
    NODE_ENV: ENV.NODE_ENV,
    sentry,
  });

  app.provide(DefaultApolloClient, apolloClient);

  app.use(AuthPlugin, {
    apolloClient,
    auth0ClientOptions: {
      domain: ENV.AUTH0_DOMAIN,
      clientId: ENV.AUTH0_ID,
      authorizationParams: {
        audience: ENV.AUTH0_AUDIENCE,
      },
    },
    defaultRedirectUri: `${ENV.BASE_URL}/loading`,
    logoutReturnTo: ENV.BASE_URL,
  });

  // Router must be installed AFTER AuthPlugin so guards can access getInstance()
  app.use(router);

  router.onError((err) => {
    console.error('[Router error]', err);
    sentry.captureException(err);
  });

  app.config.errorHandler = (err) => {
    console.error('[Vue error]', err);
    sentry.captureException(err as Error);
  };

  app.mount('#app');
})().catch((err) => {
  console.error('App initialization failed:', err);
});
